import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Upload, Trash2, LogOut, Image, GripVertical, 
  Check, X, Loader2, HardHat, Droplets, Layers, 
  Paintbrush, Hammer, Home
} from "lucide-react";
import { compressImage, MAX_UPLOAD_BYTES } from "../lib/compressImage";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const serviceOptions = [
  { value: "aannemer", label: "Aannemer", icon: HardHat },
  { value: "badkamer", label: "Badkamer Renovatie", icon: Droplets },
  { value: "stukadoor", label: "Stukadoor", icon: Layers },
  { value: "schilderwerk", label: "Schilderwerk", icon: Paintbrush },
  { value: "timmerwerk", label: "Timmerwerk", icon: Hammer },
  { value: "homepage", label: "Homepage", icon: Home },
];

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authHeader, setAuthHeader] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [selectedService, setSelectedService] = useState("aannemer");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Check for saved auth on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem("adminAuth");
    if (savedAuth) {
      setAuthHeader(savedAuth);
      setIsLoggedIn(true);
    }
  }, []);

  // Load photos when service changes or logged in
  useEffect(() => {
    if (isLoggedIn) {
      loadPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, selectedService]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      const header = `Basic ${auth}`;
      
      await axios.post(`${API}/admin/login`, {}, {
        headers: { Authorization: header }
      });
      
      setAuthHeader(header);
      localStorage.setItem("adminAuth", header);
      setIsLoggedIn(true);
      toast.success("Ingelogd als admin");
    } catch (err) {
      toast.error("Ongeldige inloggegevens");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setAuthHeader("");
    setIsLoggedIn(false);
    setCredentials({ username: "", password: "" });
    toast.success("Uitgelogd");
  };

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/photos?service=${selectedService}`, {
        headers: { Authorization: authHeader }
      });
      setPhotos(response.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        toast.error("Sessie verlopen, log opnieuw in");
      } else {
        toast.error("Kon foto's niet laden");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (files.length > 5) {
      toast.error("Maximaal 5 foto's per keer");
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    if (files.some((f) => f.size > MAX_UPLOAD_BYTES)) {
      toast.error("Een of meer bestanden zijn groter dan 50MB");
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    setUploading(true);
    
    try {
      const prepared = await Promise.all(files.map((f) => compressImage(f)));
      const formData = new FormData();
      prepared.forEach(file => {
        formData.append('files', file);
      });
      
      const uploadResponse = await axios.post(`${API}/upload-multiple`, formData, {
        timeout: 120000
      });
      
      const uploaded = uploadResponse.data.uploaded || [];
      const errors = uploadResponse.data.errors || [];
      errors.forEach((er) => toast.error(`${er.filename}: ${er.reason}`));
      
      if (uploaded.length === 0) {
        if (errors.length === 0) toast.error("Upload mislukt - controleer bestandstype (JPG/PNG/WEBP)");
        return;
      }
      
      const urls = uploaded.map(p => p.url);
      
      // Add to database
      await axios.post(`${API}/admin/photos/bulk`, {
        urls: urls,
        service: selectedService
      }, {
        headers: { Authorization: authHeader },
        timeout: 30000
      });
      
      toast.success(`${urls.length} foto('s) toegevoegd!`);
      loadPhotos();
      
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Upload mislukt';
      toast.error(`Fout: ${errorMsg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deletePhoto = async (photoId) => {
    if (!confirm("Weet u zeker dat u deze foto wilt verwijderen?")) return;
    
    try {
      await axios.delete(`${API}/admin/photos/${photoId}`, {
        headers: { Authorization: authHeader }
      });
      toast.success("Foto verwijderd");
      loadPhotos();
    } catch (err) {
      toast.error("Kon foto niet verwijderen");
    }
  };

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#005F5F] mx-auto mb-4 flex items-center justify-center">
              <Image className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A]">
              Admin Dashboard
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              Cardoso Bouw en Onderhoud
            </p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Gebruikersnaam
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full h-12 px-4 border border-slate-300 focus:border-[#005F5F] focus:ring-1 focus:ring-[#005F5F] outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Wachtwoord
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full h-12 px-4 border border-slate-300 focus:border-[#005F5F] focus:ring-1 focus:ring-[#005F5F] outline-none"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full h-12 bg-[#005F5F] hover:bg-[#004a4a] text-white font-bold uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loginLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Inloggen"
              )}
            </button>
          </form>
          
          <button
            onClick={() => navigate("/")}
            className="w-full mt-4 text-sm text-slate-500 hover:text-[#005F5F] transition-colors"
          >
            ← Terug naar website
          </button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#005F5F] text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image className="w-6 h-6" />
            <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase">
              Foto Beheer
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Uitloggen</span>
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Service Tabs */}
        <div className="bg-white p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {serviceOptions.map((service) => (
              <button
                key={service.value}
                onClick={() => setSelectedService(service.value)}
                className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${
                  selectedService === service.value
                    ? "bg-[#005F5F] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <service.icon className="w-4 h-4" />
                {service.label}
              </button>
            ))}
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white p-6 mb-6 shadow-sm">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0F172A] mb-4">
            Foto's uploaden naar {serviceOptions.find(s => s.value === selectedService)?.label}
          </h2>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-3 px-6 py-4 border-2 border-dashed border-slate-300 hover:border-[#005F5F] text-slate-600 hover:text-[#005F5F] transition-colors w-full justify-center disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Uploaden...</span>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6" />
                <span>Klik om foto's te selecteren (meerdere tegelijk mogelijk)</span>
              </>
            )}
          </button>
          <p className="text-xs text-slate-500 mt-2">
            Ondersteunde formaten: JPG, PNG, WEBP, HEIC. Max 50MB per bestand, grote foto's worden automatisch verkleind.
          </p>
        </div>

        {/* Photos Grid */}
        <div className="bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0F172A]">
              Huidige foto's ({photos.length})
            </h2>
            {loading && <Loader2 className="w-5 h-5 animate-spin text-[#005F5F]" />}
          </div>
          
          {photos.length === 0 && !loading ? (
            <div className="text-center py-12 text-slate-500">
              <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nog geen foto's voor deze dienst</p>
              <p className="text-sm mt-1">Upload foto's om ze hier te zien</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative group aspect-square bg-slate-100 border border-slate-200 overflow-hidden"
                >
                  <img
                    src={photo.url.startsWith('http') ? photo.url : `${process.env.REACT_APP_BACKEND_URL || ''}${photo.url}`}
                    alt={photo.alt || "Project foto"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100%" height="100%"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">Geen beeld</text></svg>';
                    }}
                  />
                  
                  {/* Delete overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => deletePhoto(photo.id)}
                      className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Order badge */}
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1">
                    #{photo.order + 1}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Back to website */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-slate-500 hover:text-[#005F5F] transition-colors"
          >
            ← Terug naar website
          </button>
        </div>
      </div>
    </div>
  );
}
